// Design Ref: dividend-tax-tracking.design.md §1, §3, §9
// Plan SC: SC-01 (USD auto), SC-02 (exempt), SC-03 (KRW regression), SC-04 (backfill), SC-05 (yearly)
//
// Pure helpers for dividend tax calculation, backfill, yearly aggregation.
// USD only — KRW dividends keep existing manual input pattern.
//
// 배당 세후 자동 계산 세율 — 사용자 요청(2026-09-25)으로 국내·미국 모두 15% → 세후 = 세전 × 0.85
// (참고: 법정 국내 원천징수는 소득세 14% + 지방세 1.4% = 15.4%. 금융소득 종합과세 추정은 법정 14% 기준 유지)

export const TAX_RATE_USD = 0.15;
export const TAX_RATE_KR = 0.15;
const taxRateFor = (currency) => (currency === 'USD' ? TAX_RATE_USD : TAX_RATE_KR);

/**
 * 세금·세후 자동 계산 (USD 만).
 * @param {object} args
 * @param {string|number} args.amount   세전 배당금
 * @param {'KRW'|'USD'} args.currency
 * @param {boolean} args.taxExempt      면세 여부 (특수계좌/비과세)
 * @returns {{tax: number|'', afterTax: number|''}}
 */
export function calcDividendTax({ amount, currency, taxExempt }) {
  if (amount === '' || amount === null || amount === undefined) return { tax: '', afterTax: '' };
  const amt = parseFloat(amount);
  if (!Number.isFinite(amt)) return { tax: '', afterTax: '' };
  if (taxExempt) return { tax: 0, afterTax: +amt.toFixed(2) };
  if (currency !== 'USD') return { tax: '', afterTax: '' };  // KRW: 사용자 수동
  const tax = +(amt * TAX_RATE_USD).toFixed(2);
  const afterTax = +(amt - tax).toFixed(2);
  return { tax, afterTax };
}

/**
 * 입력 모달 파생값 계산 (dirty 우선).
 * - 세후(afterTax): 면세→세전 / 부분과세(주당 세금)→세전−주당세금×주수 / 일반(국내·미국) 자동 세전×0.85
 * - 세금(tax): 면세→0 / 그 외 = 세전 − 세후 (둘 다 숫자일 때)
 * - 주당배당금(perShare): 세전 ÷ 주수 (주수>0일 때). USD 소수 4자리, KRW 2자리
 * dirty[field]=true 면 그 필드는 자동 계산 skip (수동 우선).
 * @returns {{afterTax?, tax?, perShare?}} 변경할 필드만 담음 (undefined 키는 유지)
 */
export function computeDividendDerived({ amount, afterTax, shares, currency, taxExempt, taxBasePerShare, taxPerShare }, dirty = {}) {
  const out = {};
  const amt = parseFloat(amount);
  const hasAmt = Number.isFinite(amt);
  const sh = parseFloat(shares);
  const tbps = parseFloat(taxBasePerShare);
  const tps = parseFloat(taxPerShare);
  // 부분과세 종목(커버드콜 등): 1주당 세금 × 주수 = 세금 (가장 우선)
  const hasTaxPerShare = Number.isFinite(tps) && tps >= 0 && String(taxPerShare).trim() !== '' && Number.isFinite(sh) && sh > 0;
  // 기본감세 종목: 주당과세표준 × 주수 가 과세표준 (세금은 여기에만 부과)
  const hasTaxBase = !hasTaxPerShare && Number.isFinite(tbps) && tbps >= 0 && String(taxBasePerShare ?? '').trim() !== '' && Number.isFinite(sh) && sh > 0; // 0 = 이번 분배 전액 비과세

  // 1) 세금
  if (taxExempt) {
    out.tax = 0;
  } else if (hasTaxPerShare) {
    if (!dirty.tax) out.tax = +(tps * sh).toFixed(2);
  } else if (hasTaxBase) {
    // 기본감세: 세금 = (주당과세표준 × 주수) × 15%
    if (!dirty.tax) out.tax = +(tbps * sh * taxRateFor(currency)).toFixed(2);
  }
  // (일반 케이스의 세금은 세후 계산 후 = 세전−세후, 아래에서)

  // 2) 세후
  if (taxExempt) {
    out.afterTax = hasAmt ? +amt.toFixed(2) : '';
  } else if (hasTaxBase || hasTaxPerShare) {
    // 세후 = 세전 − 세금 (과세표준·주당세금 기반)
    if (!dirty.afterTax && hasAmt) {
      const t = out.tax !== undefined ? out.tax : (parseFloat(afterTax) ? amt - parseFloat(afterTax) : 0);
      out.afterTax = +(amt - t).toFixed(2);
    }
  } else if (!dirty.afterTax && hasAmt) {
    // 일반 과세: 국내·미국 모두 15% → 세후 = 세전 × 0.85 (KRW 는 원 단위 반올림)
    const after = amt * (1 - taxRateFor(currency));
    out.afterTax = currency === 'USD' ? +after.toFixed(2) : Math.round(after);
  }

  // 일반 케이스 세금 = 세전 − 세후
  if (!taxExempt && !hasTaxBase && !hasTaxPerShare && !dirty.tax) {
    const effAfter = out.afterTax !== undefined ? out.afterTax : afterTax;
    const aft = parseFloat(effAfter);
    if (hasAmt && Number.isFinite(aft)) out.tax = +(amt - aft).toFixed(2);
  }

  // 3) 주당배당금 = 세전 ÷ 주수
  if (!dirty.perShare) {
    if (hasAmt && Number.isFinite(sh) && sh > 0) {
      out.perShare = currency === 'USD' ? +(amt / sh).toFixed(4) : +(amt / sh).toFixed(2);
    }
  }
  return out;
}

/** 기존 dividend row 가 backfill 대상인가? tax 필드가 undefined 이면 마이그레이션 전 */
export function needsBackfill(d) {
  return d && d.tax === undefined;
}

/**
 * 의심 행: 사용자가 비표준 입력한 가능성 — backfill skip 후 warn.
 *   - amount <= 0: 의미 없음
 *   - afterTax > amount × 1.05: 세후가 세전보다 큰 비정상 (5% 오차 허용)
 *   - afterTax < 0: 음수 비정상
 */
export function isSuspicious(d) {
  const amt = parseFloat(d.amount);
  const aft = parseFloat(d.afterTax);
  if (!Number.isFinite(amt) || amt <= 0) return false;  // amount 없으면 의심 아님 (그냥 신규)
  if (d.afterTax === '' || d.afterTax === null || d.afterTax === undefined) return false;
  if (!Number.isFinite(aft)) return true;
  if (aft < 0) return true;
  if (aft > amt * 1.05) return true;
  return false;
}

/** 단일 row backfill — 의심 행은 그대로 반환 (호출자가 warn) */
export function backfillRow(d) {
  if (!needsBackfill(d)) return d;
  if (isSuspicious(d)) return d;

  const taxExempt = false;
  if (d.currency !== 'USD') {
    return { ...d, tax: '', taxExempt };
  }

  // USD 처리
  const amt = parseFloat(d.amount);
  if (!Number.isFinite(amt) || amt <= 0) {
    return { ...d, tax: '', taxExempt };
  }
  const hasAfter = d.afterTax !== '' && d.afterTax !== null && d.afterTax !== undefined;
  if (hasAfter) {
    const aft = parseFloat(d.afterTax);
    const tax = +(amt - aft).toFixed(2);
    return { ...d, tax, taxExempt };
  }
  // afterTax 비어있음 → 자동 계산
  const tax = +(amt * TAX_RATE_USD).toFixed(2);
  const afterTax = +(amt - tax).toFixed(2);
  return { ...d, tax, afterTax, taxExempt };
}

/**
 * 배열 전체 backfill. 의심 행은 console.warn 후 skip.
 * @returns {{migrated: object[], suspicious: object[], migratedCount: number}}
 */
export function backfillDividends(arr) {
  if (!Array.isArray(arr)) return { migrated: [], suspicious: [], migratedCount: 0 };
  const suspicious = [];
  let migratedCount = 0;
  const migrated = arr.map(d => {
    if (!needsBackfill(d)) return d;
    if (isSuspicious(d)) {
      suspicious.push(d);
      return d;
    }
    migratedCount++;
    return backfillRow(d);
  });
  return { migrated, suspicious, migratedCount };
}

/**
 * 연도별 합계 — 과세 / 면세 분리. USD only.
 * 환율: 배당별 수령일 환율(d.rate) 우선, 없으면 현재 rate fallback.
 *
 * @returns {Array<{year, taxable:{pre,tax,after,krw}, exempt:{total,krw}, count, exemptCount}>}
 *          최신 연도 위 (descending)
 */
export function aggregateByYear(dividends, rate) {
  if (!Array.isArray(dividends)) return [];
  const fallback = Number.isFinite(rate) && rate > 0 ? rate : 1400;
  const byYear = {};

  for (const d of dividends) {
    if (d.currency !== 'USD') continue;
    const year = (d.date || '').slice(0, 4);
    if (!year) continue;
    if (!byYear[year]) {
      byYear[year] = {
        year,
        taxable: { pre: 0, tax: 0, after: 0, krw: 0 },
        exempt: { total: 0, krw: 0 },
        count: 0,
        exemptCount: 0,
      };
    }
    const bucket = byYear[year];
    bucket.count++;
    const amt = parseFloat(d.amount) || 0;
    const r = (d.rate && parseFloat(d.rate) > 0) ? parseFloat(d.rate) : fallback;  // 수령일 환율
    if (d.taxExempt) {
      bucket.exempt.total += amt;
      bucket.exempt.krw += amt * r;
      bucket.exemptCount++;
    } else {
      const tax = parseFloat(d.tax) || 0;
      const after = parseFloat(d.afterTax) || (amt - tax);
      bucket.taxable.pre += amt;
      bucket.taxable.tax += tax;
      bucket.taxable.after += after;
      bucket.taxable.krw += after * r;
    }
  }

  return Object.values(byYear)
    .map(b => ({
      ...b,
      taxable: {
        pre: +b.taxable.pre.toFixed(2),
        tax: +b.taxable.tax.toFixed(2),
        after: +b.taxable.after.toFixed(2),
        krw: Math.round(b.taxable.krw),
      },
      exempt: {
        total: +b.exempt.total.toFixed(2),
        krw: Math.round(b.exempt.krw),
      },
    }))
    .sort((a, b) => b.year.localeCompare(a.year));
}

// ─────────────────────────────────────────────────────────────
// 금융소득 종합과세 (2026 기준)
// - 이자+배당 등 금융소득이 연 2,000만원 초과 → 초과분을 다른 종합소득과 합산해 누진세율(6~45%)
// - 비교과세: max(일반산출세액, 비교산출세액)
//     일반 = 2,000만원×14% + 누진세(다른소득 과세표준 + 초과분)
//     비교 = 금융소득 전체×14% + 누진세(다른소득 과세표준)
//   → 원천징수(14%)로 이미 낸 부분을 빼면 추가 납부 = max(0, 일반 − 비교)
//      = max(0, 누진세(B+E) − 누진세(B) − 14%×E)   (B=다른소득 과세표준, E=2,000만원 초과분)
// - 지방소득세 10% 별도
// 미반영(추정치): Gross-up·배당세액공제, 외국납부세액공제 한도, 고배당기업 배당 분리과세(2026~28), 건강보험료
// ─────────────────────────────────────────────────────────────
export const FIN_INCOME_THRESHOLD = 20000000;
export const FIN_WITHHOLDING_RATE = 0.14;
export const LOCAL_TAX_RATIO = 0.1;
// [과세표준 상한, 세율, 누진공제]
export const PROGRESSIVE_BRACKETS = [
  [14000000, 0.06, 0],
  [50000000, 0.15, 1260000],
  [88000000, 0.24, 5760000],
  [150000000, 0.35, 15440000],
  [300000000, 0.38, 19940000],
  [500000000, 0.40, 25940000],
  [1000000000, 0.42, 35940000],
  [Infinity, 0.45, 65940000],
];

/** 종합소득 기본세율 산출세액 (국세, 원) */
export function progressiveTax(base) {
  const b = Math.max(0, Number(base) || 0);
  for (const [limit, rate, deduction] of PROGRESSIVE_BRACKETS) {
    if (b <= limit) return Math.max(0, b * rate - deduction);
  }
  return 0;
}

/** 과세표준이 속한 구간 세율 */
export function marginalRate(base) {
  const b = Math.max(0, Number(base) || 0);
  for (const [limit, rate] of PROGRESSIVE_BRACKETS) if (b <= limit) return rate;
  return 0.45;
}

/**
 * 배당 1건의 과세 대상 금액 (원화).
 * - 면세(ISA·연금 등) → 0 (금융소득에서 제외)
 * - 기본감세 종목(주당과세표준 입력) → 주당과세표준 × 주수
 * - USD → 수령일 환율(d.rate) 우선, 없으면 현재 환율
 */
export function taxableDividendKrw(d, fallbackRate) {
  if (!d || d.taxExempt) return 0;
  const amt = parseFloat(d.amount) || 0;
  const sh = parseFloat(d.shares);
  const tbps = parseFloat(d.taxBasePerShare);
  const tps = parseFloat(d.taxPerShare);
  let base = amt;
  if (Number.isFinite(tps) && String(d.taxPerShare ?? '').trim() !== '' && Number.isFinite(sh) && sh > 0) {
    base = (tps * sh) / taxRateFor(d.currency);   // 부분과세: 세금 ÷ 세율 = 과세 대상 금액
  } else if (Number.isFinite(tbps) && tbps >= 0 && String(d.taxBasePerShare ?? '').trim() !== '' && Number.isFinite(sh) && sh > 0) {
    base = tbps * sh;
  }
  if (d.currency === 'USD') {
    const r = parseFloat(d.rate) > 0 ? parseFloat(d.rate) : (Number.isFinite(fallbackRate) && fallbackRate > 0 ? fallbackRate : 1400);
    return base * r;
  }
  return base;
}

/**
 * 금융소득 종합과세 추정.
 * @param {number} financialIncome 연간 금융소득 합계 (원)
 * @param {number} otherBase 다른 종합소득 과세표준 (근로·사업 등, 공제 후, 원)
 */
export function estimateFinancialIncomeTax(financialIncome, otherBase = 0) {
  const F = Math.max(0, Number(financialIncome) || 0);
  const B = Math.max(0, Number(otherBase) || 0);
  const over = Math.max(0, F - FIN_INCOME_THRESHOLD);
  const withheld = F * FIN_WITHHOLDING_RATE;
  if (over <= 0) {
    return { comprehensive: false, over: 0, remaining: FIN_INCOME_THRESHOLD - F, withheld, generalTax: null, compareTax: null, extra: 0, extraWithLocal: 0, topRate: null };
  }
  const generalTax = FIN_INCOME_THRESHOLD * FIN_WITHHOLDING_RATE + progressiveTax(B + over);
  const compareTax = F * FIN_WITHHOLDING_RATE + progressiveTax(B);
  const extra = Math.max(0, generalTax - compareTax);
  return {
    comprehensive: true, over, remaining: 0, withheld,
    generalTax: Math.round(generalTax), compareTax: Math.round(compareTax),
    extra: Math.round(extra), extraWithLocal: Math.round(extra * (1 + LOCAL_TAX_RATIO)),
    topRate: marginalRate(B + over),
  };
}

/**
 * 연도별 금융소득 집계 + 종합과세 추정.
 * @param {Array} dividends 배당 기록 (KRW/USD)
 * @param {number} rate 현재 환율 (수령일 환율 없을 때)
 * @param {Object} settings { [year]: { interest, otherBase } } — 사용자가 입력한 이자소득·다른소득 과세표준
 * @param {string[]} extraYears 배당이 없어도 보여줄 연도 (예: 올해)
 * @returns 최신 연도 위
 */
export function financialIncomeByYear(dividends, rate, settings = {}, extraYears = []) {
  const byYear = {};
  const ensure = (year) => (byYear[year] = byYear[year] || { year, domesticKrw: 0, foreignKrw: 0, exemptKrw: 0, count: 0, stocks: {} });
  for (const d of Array.isArray(dividends) ? dividends : []) {
    const year = (d.date || '').slice(0, 4);
    if (!/^\d{4}$/.test(year)) continue;
    const y = ensure(year);
    y.count++;
    const amt = parseFloat(d.amount) || 0;
    const r = d.currency === 'USD' ? (parseFloat(d.rate) > 0 ? parseFloat(d.rate) : rate || 1400) : 1;
    // 종목별 구성 (상세보기)
    const key = String(d.name || '(이름 없음)').replace(/\s+/g, '').toLowerCase() + '|' + (d.currency === 'USD' ? 'USD' : 'KRW');
    const st = y.stocks[key] = y.stocks[key] || { name: d.name || '(이름 없음)', currency: d.currency === 'USD' ? 'USD' : 'KRW', count: 0, grossKrw: 0, grossOrig: 0, taxableKrw: 0, exemptKrw: 0, withheldKrw: 0, kinds: new Set(), lastDate: '' };
    st.count++;
    st.grossKrw += amt * r;
    st.grossOrig += amt;
    if ((d.date || '') > st.lastDate) st.lastDate = d.date;
    if (d.taxExempt) {
      y.exemptKrw += amt * r;
      st.exemptKrw += amt * r;
      st.kinds.add('exempt');
      continue;
    }
    const krw = taxableDividendKrw(d, rate);
    if (d.currency === 'USD') y.foreignKrw += krw; else y.domesticKrw += krw;
    st.taxableKrw += krw;
    st.withheldKrw += (parseFloat(d.tax) || 0) * r;
    const has = (v) => v !== '' && v !== undefined && v !== null && Number.isFinite(parseFloat(v));
    st.kinds.add(has(d.taxPerShare) ? 'partial' : has(d.taxBasePerShare) ? 'taxbase' : 'normal');
  }
  extraYears.forEach(ensure);
  Object.keys(settings || {}).forEach(y => { if (/^\d{4}$/.test(y)) ensure(y); });
  return Object.values(byYear).map(y => {
    const s = (settings || {})[y.year] || {};
    const interest = Math.max(0, parseFloat(s.interest) || 0);
    const otherBase = Math.max(0, parseFloat(s.otherBase) || 0);
    const dividendKrw = y.domesticKrw + y.foreignKrw;
    const total = dividendKrw + interest;
    return {
      year: y.year,
      domesticKrw: Math.round(y.domesticKrw), foreignKrw: Math.round(y.foreignKrw), exemptKrw: Math.round(y.exemptKrw),
      dividendKrw: Math.round(dividendKrw), interest, otherBase, hasOtherBase: s.otherBase !== undefined && s.otherBase !== '',
      total: Math.round(total), count: y.count,
      stocks: Object.values(y.stocks).map(st => {
        const kinds = [...st.kinds];
        const kind = kinds.length === 1 ? kinds[0] : (kinds.includes('exempt') && kinds.length === 2 ? 'mixed-exempt' : 'mixed');
        return {
          name: st.name, currency: st.currency, count: st.count, lastDate: st.lastDate, kind,
          grossKrw: Math.round(st.grossKrw), grossOrig: +st.grossOrig.toFixed(2),
          taxableKrw: Math.round(st.taxableKrw), exemptKrw: Math.round(st.exemptKrw),
          reducedKrw: Math.max(0, Math.round(st.grossKrw - st.exemptKrw - st.taxableKrw)), // 부분과세·과세표준으로 빠진 몫
          withheldKrw: Math.round(st.withheldKrw),
          share: dividendKrw > 0 ? st.taxableKrw / dividendKrw : 0,
        };
      }).sort((a, b) => b.taxableKrw - a.taxableKrw || b.grossKrw - a.grossKrw),
      progress: Math.min(1, total / FIN_INCOME_THRESHOLD),
      ...estimateFinancialIncomeTax(total, otherBase),
    };
  }).sort((a, b) => b.year.localeCompare(a.year));
}
